import { useState } from 'react'
import { Button } from '@/common/ui/button'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@/common/ui/dialog'
import { DataTablePaginate } from '@/common/ui/table/data-table-paginate'
import { DataTableFilterSelector } from '@/common/ui/table/data-table-selector-with-query'
import { DataTableSimpleSearch } from '@/common/ui/table/data-table-simple-search'
import useMutationDeleteShopItem from '@/modules/shop/api/hooks/useMutationDeleteShopItem'
import {
	createShopTableColumns,
	STATUS_OPTIONS,
	REWARD_TYPE_OPTIONS,
	CURRENCY_TYPE_OPTIONS_FILTER,
} from './constants'
import type { components } from '@/schema'

type ShopItem = components['schemas']['AdminShopItemResponse'] & {
	price?: number
}

interface ShopTableProps {
	data: ShopItem[]
	total: number
	isLoading?: boolean
}

export default function ShopTable({ data, total, isLoading }: ShopTableProps) {
	const [deleteId, setDeleteId] = useState<string | null>(null)
	const { mutate: deleteShopItem, isPending: isDeleting } =
		useMutationDeleteShopItem()

	const handleDelete = (id: string) => {
		deleteShopItem(id, {
			onSuccess: () => {
				setDeleteId(null)
			},
		})
	}

	const columns = createShopTableColumns({
		onDelete: setDeleteId,
	})

	return (
		<>
			<div className="space-y-4">
				{/* Filters */}
				<div className="flex flex-wrap items-end gap-4">
					<DataTableSimpleSearch placeHolder="ค้นหาชื่อสินค้า" />

					<DataTableFilterSelector
						options={STATUS_OPTIONS}
						placeholder="เลือกสถานะ"
						label="สถานะ"
						filterKey="isActive"
						className="w-48"
					/>
					<DataTableFilterSelector
						options={REWARD_TYPE_OPTIONS}
						placeholder="เลือกประเภทรางวัล"
						label="ประเภทรางวัล"
						filterKey="rewardType"
						className="w-52"
					/>
					<DataTableFilterSelector
						options={CURRENCY_TYPE_OPTIONS_FILTER}
						placeholder="เลือกสกุลเงิน"
						label="สกุลเงิน"
						filterKey="currencyType"
						className="w-48"
					/>
				</div>

				{/* Table */}
				<DataTablePaginate
					columns={columns}
					data={data}
					totalItems={total}
					isLoading={isLoading}
				/>
			</div>

			{/* Delete Confirmation Dialog */}
			<Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>ยืนยันการลบสินค้า</DialogTitle>
					</DialogHeader>
					<div className="py-4">
						<p>
							คุณแน่ใจหรือไม่ที่จะลบสินค้านี้? การดำเนินการนี้ไม่สามารถยกเลิกได้
						</p>
					</div>
					<div className="flex justify-end gap-2">
						<Button variant="outline" onClick={() => setDeleteId(null)}>
							ยกเลิก
						</Button>
						<Button
							variant="destructive"
							disabled={isDeleting}
							onClick={() => deleteId && handleDelete(deleteId)}
						>
							{isDeleting ? 'กำลังลบ...' : 'ลบ'}
						</Button>
					</div>
				</DialogContent>
			</Dialog>
		</>
	)
}
